import { FileImageFilled, MoreOutlined } from '@ant-design/icons'
import { DriveFile, OrganizationMember } from '@prisma/client'
import { Button, Card, Dropdown, Flex, Typography } from 'antd'
import { useState } from 'react'
import { styled } from 'styled-components'
import { OrganizationMemberFromContext } from '~/core/context/organization/hooks/useOrganizationMembers'
import { RenderMarkdownContent } from '~/features/chatV3/client/components/Thread/components/RenderMarkdownContent'
import { Icon } from '~/features/shared/icons'
import { Toolbox } from '~/plugins/toolbox/shared'
import { ModalPublish } from '../../../ModalPublish'

export const PreviewHover = styled.div`
  position: relative;
  border-radius: 8px;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0);
    transition: background 0.2s ease;
    pointer-events: none;
  }

  &:hover::after {
    background: rgba(0, 0, 0, 0.04);
  }
`

export const HoverActions = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 2;

  ${PreviewHover}:hover & {
    opacity: 1;
  }

  &.show-actions {
    opacity: 1;
  }
`

type Props = {
  organizationMember: OrganizationMember
  agent: OrganizationMemberFromContext
  file: DriveFile
  onSelect: (driveFileId: string) => void
  onDelete: (driveFile: DriveFile) => void
  onPublish: (driveFileId: string, platform: string) => void
}

export const FileCard: React.FC<Props> = ({
  agent,
  organizationMember,
  file,
  onSelect,
  onDelete,
  onPublish,
}) => {
  const [isPublish, setIsPublish] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const handlePublish = (platform: string) => {
    onPublish(file.id, platform)
  }

  if (!file) {
    return <>No file found</>
  }

  if (['plain/text', 'text/plain'].includes(file.mimetype)) {
    return (
      <>
        <div style={{ maxWidth: '600px' }}>
          <Card
            onClick={() => onSelect(file.id)}
            style={{ border: 'none' }}
            styles={{ body: { padding: '0px' } }}
          >
            <div className="mr-2 ml-2">
              <PreviewHover>
                <HoverActions
                  className={`hover-actions ${isDropdownOpen ? 'show-actions' : ''}`}
                >
                  <Button
                    title="Publish"
                    size="small"
                    type="default"
                    icon={<Icon name="paper-airplane" />}
                    onClick={e => {
                      e.stopPropagation()
                      setIsPublish(true)
                    }}
                  />
                  <Dropdown
                    menu={{
                      items: [
                        {
                          label: 'Edit',
                          key: 'edit',
                          onClick: ({ domEvent }) => {
                            domEvent.stopPropagation()
                            onSelect(file.id)
                          },
                        },
                        {
                          label: 'Delete',
                          key: 'delete',
                          onClick: ({ domEvent }) => {
                            domEvent.stopPropagation()
                            onDelete(file)
                          },
                        },
                      ],
                    }}
                    trigger={['hover']}
                    onOpenChange={setIsDropdownOpen}
                    open={isDropdownOpen}
                  >
                    <Button
                      title="More"
                      size="small"
                      type="default"
                      icon={<MoreOutlined />}
                      onClick={e => {
                        e.stopPropagation()
                      }}
                    />
                  </Dropdown>
                </HoverActions>
                <Card
                  style={{
                    cursor: 'pointer',
                    height: '250px',
                    width: '100%',
                    overflowY: 'hidden',
                    overflowX: 'hidden',
                    padding: 0,
                  }}
                >
                  <Flex
                    vertical
                    gap={8}
                    align="flex-start"
                    justify="flex-start"
                    style={{
                      transform: 'scale(0.5)',
                      transformOrigin: 'top left',

                      width: '200%', // Compensate for the scaling (1/0.85 ≈ 1.18)
                    }}
                  >
                    <RenderMarkdownContent content={file.content} />
                  </Flex>
                </Card>
              </PreviewHover>
            </div>
          </Card>
        </div>
        {isPublish && (
          <ModalPublish
            blogPostId={file.id}
            content={file.content}
            onClose={() => setIsPublish(false)}
            onPublish={handlePublish}
          />
        )}
      </>
    )
  }

  return (
    <>
      <div style={{ maxWidth: '600px' }}>
        <Card
          onClick={() => onSelect(file.id)}
          style={{
            cursor: 'pointer',
            borderRadius: '12px',
            backgroundColor: '#fcfcfd',
          }}
          styles={{ body: { padding: '0px' } }}
        >
          <Flex
            align="center"
            justify="space-between"
            className="mr-3 ml-3 mt-2 mb-1"
          >
            <Flex align="center">
              <Typography.Text
                ellipsis
                style={{ margin: 0, maxWidth: '230px', fontSize: '12px' }}
              >
                {file.name}
              </Typography.Text>
            </Flex>
            <Dropdown
              menu={{
                items: [
                  {
                    label: 'Edit',
                    key: 'edit',
                    onClick: () => onSelect(file.id),
                  },
                  {
                    label: 'Delete',
                    key: 'delete',
                    onClick: () => onDelete(file),
                  },
                ],
              }}
              trigger={['click']}
            >
              <MoreOutlined
                style={{ cursor: 'pointer' }}
                onClick={e => e.stopPropagation()}
              />
            </Dropdown>
          </Flex>
          <div className="mr-2 ml-2">
            <Card
              style={{
                height: '250px',
                width: '100%',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FileImageFilled style={{ fontSize: '40px' }} />
            </Card>
          </div>
          <Flex justify="end" gap="8px" className="p-2">
            <Typography.Text type="secondary">
              {Toolbox.Date.toAsanaStyle(file.createdAt)}
            </Typography.Text>
          </Flex>
        </Card>
      </div>
    </>
  )
}
